import type { PackageProviderConfig } from '@openx/types/package/packageProviderConfig';
import type React from 'react';
import { createContext, useContext, useMemo } from 'react';

export interface PackageProviderProps {
  children: React.ReactNode;
  packageConfig: PackageProviderConfig;
}

export type PackageContext = PackageProviderConfig;

const packageContext = createContext<PackageContext>({
  isOa: true,
  isOaSuborg: false,
  useAccountOptionsFetch: () => {
    throw new Error('useAccountOptionsFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  useCloneForm: () => {
    throw new Error('useCloneForm: Not implemented. Ensure you are inside the PackageContext');
  },
  useCreateForm: () => {
    throw new Error('useCreateForm: Not implemented. Ensure you are inside the PackageContext');
  },
  useDealsFetch: () => {
    throw new Error('useDealsFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  useDetailsConfig: () => {
    throw new Error('useDetailsConfig: Not implemented. Ensure you are inside the PackageContext');
  },
  useDispatchGetNetworkAndPublishersAccountsOptions: () => {
    throw new Error(
      'useDispatchGetNetworkAndPublishersAccountsOptions: Not implemented. Ensure you are inside the PackageContext'
    );
  },
  useDeviceTypesOptionsFetch: () => {
    throw new Error('useDeviceTypesOptionsFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  useDistributionChannelOptions: () => {
    throw new Error('useDistributionChannelOptions: Not implemented. Ensure you are inside the PackageContext');
  },
  useOldDistributionChannelOptions: () => {
    throw new Error('useOldDistributionChannelOptions: Not implemented. Ensure you are inside the PackageContext');
  },
  useFloorsFetch: () => {
    throw new Error('useFloorsFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  usePackageClone: () => {
    throw new Error('usePackageClone: Not implemented. Ensure you are inside the PackageContext');
  },
  usePackageCreate: () => {
    throw new Error('usePackageCreate: Not implemented. Ensure you are inside the PackageContext');
  },
  usePackageUpdate: () => {
    throw new Error('usePackageUpdate: Not implemented. Ensure you are inside the PackageContext');
  },
  usePackageFetch: () => {
    throw new Error('usePackageFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  usePackageLazyFetch: () => {
    throw new Error('usePackageFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  usePackageOperationActions: () => {
    throw new Error('usePackageOperationActions: Not implemented. Ensure you are inside the PackageContext');
  },
  usePackagePermissions: () => {
    throw new Error('usePackagePermissions: Not implemented. Ensure you are inside the PackageContext');
  },
  usePackageUsedBy: () => {
    throw new Error('usePackageUsedBy: Not implemented. Ensure you are inside the PackageContext');
  },
  usePackagesListFetch: () => {
    throw new Error('usePackagesListFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  useRelatedAccountsFetch: () => {
    throw new Error('useRelatedAccountsFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  useTargetablePackagesFetch: () => {
    throw new Error('useTargetablePackagesFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  useTechnologyOptionsFetch: () => {
    throw new Error('useTechnologyOptionsFetch: Not implemented. Ensure you are inside the PackageContext');
  },
  useUpdateForm: () => {
    throw new Error('useUpdateForm: Not implemented. Ensure you are inside the PackageContext');
  },
  useDealDisableRbo: () => ({
    disableDealsRbo: () => {
      throw new Error('useDealDisableRbo: Not implemented. Ensure you are inside the PackageContext');
    },
    isDisabling: false,
  }),
  usePackageRetargetingDealsValidation: () => ({
    isLoading: false,
    getIncompatibleDeals: () => null,
  }),
});

export const usePackageContext = () => useContext(packageContext);

export function PackageProvider({ children, packageConfig }: PackageProviderProps) {
  const value: PackageContext = useMemo(
    () => ({
      ...packageConfig,
    }),
    [packageConfig]
  );

  return <packageContext.Provider value={value}>{children}</packageContext.Provider>;
}
